import React, { useEffect, useState } from 'react';
import { Button, Form, Container, Row, Col, Card } from 'react-bootstrap';
import { BiPlus } from 'react-icons/bi';
import { FiPlus, FiCheckCircle } from 'react-icons/fi';
import { MdDeleteOutline, MdArrowBack } from 'react-icons/md';
import AsyncSelect from 'react-select/async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    createBrand,
    editBrand,
    getBrandDetails,
    deleteBrandCategory
} from '../../api/context/brand';

import { createSizeChart, getSizeChart, getSizeTypesAndSizes } from '../../api/context/sizechart';
import { getCategoryListingPaginate, getSubCategoryListingPaginate } from '../../api/context/manufacturer';
import SizeChartModal from '../../components/Modal/SizeChartModal';
import Loader from '../../components/loader/loader';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import imageToBase64 from 'image-to-base64/browser';
import { IoMdArrowBack } from 'react-icons/io';

const EditBrandPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [sizeChartMeta, setSizeChartMeta] = useState({ sizes: [], sizeTypes: [] });
    const [sizeChartData, setSizeChartData] = useState({});
    const [isSavingSizeChart, setIsSavingSizeChart] = useState(false);
    const [showSizeChartModal, setShowSizeChartModal] = useState(false);
    const [category, setCategory] = useState([]);
    const [subcategoryOptions, setSubcategoryOptions] = useState({});
    const [isFocusedStaff, setIsFocusedStaff] = useState(false);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());

    // Formik Setup
    const formik = useFormik({
        initialValues: {
            name: '',
            image: '',
            image_view: '',
            categories: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Brand name is required'),
            image: Yup.mixed().nullable(),
            categories: Yup.array().of(
                Yup.object().shape({
                    categoryId: Yup.string().required('Category is required'),
                    subCategories: Yup.array().min(1, 'At least one subcategory is required'),
                })
            ),
        }),
        onSubmit: async (values) => {
            setIsSavingSizeChart(true);

            // Build size chart payload
            const sizeChartPayload = buildSizeChartPayload(sizeChartData, sizeChartMeta);
            const sizeChartValues = sizeChartPayload.values;

            const payload = {
                ...values,
                sizeChartValues,
                ...(id && { id })
            };

            // Remove image_view from payload
            delete payload.image_view;

            try {
                if (id) {
                    // Update existing brand
                    await editBrand(payload)((response) => {
                        if (response?.data?.statusCode === 200) {
                            toast.success("Brand Updated Successfully");
                            navigate('/brand');
                        } else {
                            toast.error(response?.data?.message);
                        }
                    });
                } else {
                    // Create new brand
                    await createBrand(payload)((response) => {
                        if (response?.data?.statusCode === 200) {
                            toast.success("Brand Created Successfully");
                            navigate('/brand');
                        } else {
                            toast.error(response?.data?.message);
                        }
                    });
                }
            } catch (error) {
                toast.error("An error occurred while saving the brand");
            } finally {
                setIsSavingSizeChart(false);
            }
        },
    });

    const handleImageChange = async (e) => {
        const file = e?.target?.files[0];
        const maxSizeInBytes = 2 * 1024 * 1024;
        const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

        if (file?.size > maxSizeInBytes) {
            toast.error("File size exceeds 2 MB limit");
            e.target.value = null;
            return;
        }

        if (!validImageTypes.includes(file?.type)) {
            toast.error("Only jpg/jpeg and png files are allowed!");
            e.target.value = null;
            return;
        }

        const response = await imageToBase64(URL.createObjectURL(file));
        formik.setValues({
            ...formik.values,
            image: response,
            image_view: 'data:image/jpeg;base64,' + response,
        });
    };

    const getAllSubCategoryIds = () => {
        return formik.values.categories.flatMap((cat) =>
            (cat.selectedSubCategory || []).map((sub) => sub.value)
        );
    };

    const mapSizeChartArrayToObject = (apiData) => {
        const result = {};
        apiData.forEach((item) => {
            const categoryId = String(item.brandCategory.categoryId);
            const subCategoryId = String(item.brandCategory.subCategoryId);
            const size = String(item.subsubCategorySize?.size ?? '');
            const sizeType = String(item.subsubCategorySizeType?.sizeType ?? '');
            const measurement = item.measurement;

            if (!result[categoryId]) result[categoryId] = {};
            if (!result[categoryId][subCategoryId]) result[categoryId][subCategoryId] = {};
            if (!result[categoryId][subCategoryId][size]) result[categoryId][subCategoryId][size] = {};

            result[categoryId][subCategoryId][size][sizeType] = measurement;
        });
        return result;
    };

    const fetchSizeChart = () => {
        if (!id) return;

        getSizeChart({ brandId: id })((response) => {
            if (response?.data?.data) {
                const mapped = mapSizeChartArrayToObject(response.data.data);
                setSizeChartData(mapped);
            } else {
                setSizeChartData({});
            }
        });
    };

    const fetchAvailableCategories = () => {
        getCategoryListingPaginate({
            page: 1,
            limit: 100,
            search: ''
        })((response) => {
            if (response?.statusCode === 200) {
                setAvailableCategories(response.results);
            }
        });
    };

    const fetchBrandData = async () => {
        if (!id) {
            setIsLoading(false);
            fetchAvailableCategories();
            return;
        }

        getBrandDetails({ id })((response) => {
            if (response?.data?.statusCode === 200) {
                const brandData = response.data.data;

                // Group subcategories by categoryId
                const categoryMap = new Map();

                brandData.BrandCategories?.forEach(item => {
                    const categoryId = item.categoryId;

                    if (!categoryMap.has(categoryId)) {
                        categoryMap.set(categoryId, {
                            categoryId: categoryId,
                            subCategories: [],
                            selectedCategory: {
                                value: item.categoryId,
                                label: item.category?.title || `Category ${item.categoryId}`
                            },
                            selectedSubCategory: []
                        });
                    }

                    // Add subcategory to the existing category
                    const category = categoryMap.get(categoryId);
                    const subCategoryOption = {
                        value: item.subCategoryId,
                        label: item.subcategory?.title || `Subcategory ${item.subCategoryId}`
                    };

                    // Avoid duplicates
                    if (!category.subCategories.includes(item.subCategoryId)) {
                        category.subCategories.push(item.subCategoryId);
                        category.selectedSubCategory.push(subCategoryOption);
                    }
                });

                // Convert map values to array
                const transformedCategories = Array.from(categoryMap.values());

                formik.setValues({
                    name: brandData.name || '',
                    image: '',
                    image_view: brandData.image || '',
                    categories: transformedCategories,
                });

                // Update selected category IDs
                const selectedIds = new Set(transformedCategories.map(cat => cat.categoryId));
                setSelectedCategoryIds(selectedIds);

                // Pre-fetch subcategories for each selected category
                transformedCategories.forEach(cat => {
                    fetchSubcategoriesForCategory(cat.categoryId);
                });

                setIsLoading(false);
                fetchSizeChart();
                fetchAvailableCategories();
            } else {
                toast.error("Failed to fetch brand details");
                setIsLoading(false);
            }
        });
    };

    // Fetch categories dynamically based on input
    const loadOptionsCategory = (inputValue) => {
        return new Promise((resolve) => {
            const typingTimer = setTimeout(() => {
                if (inputValue.length >= 2) {
                    getCategoryListingPaginate({
                        page: 1,
                        limit: 10,
                        search: inputValue
                    })((response) => {
                        if (response?.statusCode === 200) {
                            resolve(response.results);
                            setCategory(response.results);
                        } else {
                            resolve([]);
                        }
                    });
                } else {
                    resolve([]);
                }
            }, 300);
        });
    };

    // Fetch subcategories for a specific category
    const fetchSubcategoriesForCategory = (categoryId) => {
        getSubCategoryListingPaginate({
            page: 1,
            pageSize: 100,
            search: '',
            categoryIds: [categoryId],
        })((response) => {
            if (response?.statusCode === 200) {
                setSubcategoryOptions(prev => ({
                    ...prev,
                    [categoryId]: response.results
                }));
            }
        });
    };

    // Fetch subcategories based on category ID for AsyncSelect
    const loadOptionsSubCategory = (inputValue, categoryId) => {
        return new Promise((resolve) => {
            // If we already have subcategories for this category, filter them
            if (subcategoryOptions[categoryId]) {
                const filtered = subcategoryOptions[categoryId].filter(option =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                );
                resolve(filtered);
                return;
            }

            // If we don't have subcategories yet, fetch them
            getSubCategoryListingPaginate({
                page: 1,
                pageSize: 100,
                search: inputValue,
                categoryIds: [categoryId],
            })((response) => {
                if (response?.statusCode === 200) {
                    setSubcategoryOptions(prev => ({
                        ...prev,
                        [categoryId]: response.results
                    }));
                    resolve(response.results);
                } else {
                    resolve([]);
                }
            });
        });
    };

   const handleCategorySelect = (category) => {
    if (selectedCategoryIds.has(category.value)) {
        return; // Already selected
    }

    const newCategory = {
        categoryId: category.value,
        subCategories: [], // Initialize as empty array
        selectedCategory: category,
        selectedSubCategory: [], // Initialize as empty array
    };

    formik.setFieldValue('categories', [
        ...formik.values.categories,
        newCategory,
    ]);

    setSelectedCategoryIds(prev => new Set([...prev, category.value]));

    // Immediately fetch subcategories for the selected category
    fetchSubcategoriesForCategory(category.value);
};

    const deleteCategory = (categoryId) => {
        if (id) {
            // If editing existing brand, call API to delete category
            // Note: You might need to adjust your API to delete all subcategories for this category
            deleteBrandCategory(id, categoryId)((response) => {
                if (response?.data?.statusCode === 200) {
                    toast.success("Category removed successfully");
                } else {
                    toast.error("Failed to remove category");
                }
            });
        }

        // Remove from local form state
        const updatedCategories = formik.values.categories.filter(
            cat => cat.categoryId !== categoryId
        );
        formik.setFieldValue('categories', updatedCategories);

        // Update selected category IDs
        setSelectedCategoryIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(categoryId);
            return newSet;
        });
    };

    const customNoOptionsMessage = ({ inputValue }) => {
        return inputValue.length === 0 ? "Type to search subcategories" : "No options found";
    };

    const buildSizeChartPayload = (sizeChartData, sizeChartMeta) => {
        const values = [];
        Object.entries(sizeChartData).forEach(([categoryId, subCats]) => {
            Object.entries(subCats).forEach(([subCategoryId, sizesObj]) => {
                const sizes = [];
                Object.entries(sizesObj).forEach(([sizeLabel, typeObj]) => {
                    Object.entries(typeObj).forEach(([typeLabel, measurement]) => {
                        const size = sizeChartMeta.sizes.find(
                            s => s.size === sizeLabel && String(s.subsubcategoryId) === String(subCategoryId)
                        );
                        const type = sizeChartMeta.sizeTypes.find(
                            t => t.sizeType === typeLabel && String(t.subsubcategoryId) === String(subCategoryId)
                        );

                        if (size && type && measurement !== "") {
                            sizes.push({
                                subsubCategorySizeId: size.id,
                                subsubCategorySizeTypeId: type.id,
                                measurement: Number(measurement),
                                unit: size.unit || "cm"
                            });
                        }
                    });
                });

                if (sizes.length > 0) {
                    values.push({
                        categoryId: Number(categoryId),
                        subCategoryId: Number(subCategoryId),
                        sizes
                    });
                }
            });
        });

        return { values };
    };

    const handleBack = () => {
        navigate(-1);
    };

    useEffect(() => {
        fetchBrandData();
    }, [id]);

    useEffect(() => {
        const subCategoryIds = getAllSubCategoryIds();
        const allIds = subCategoryIds.join(',');

        if (subCategoryIds.length) {
            getSizeTypesAndSizes([allIds])((response) => {
                if (response?.data) {
                    setSizeChartMeta({
                        sizes: response.data.sizes,
                        sizeTypes: response.data.sizeTypes,
                    });
                }
            });
        } else {
            setSizeChartMeta({ sizes: [], sizeTypes: [] });
        }
    }, [formik.values.categories]);

    if (isLoading) {
        return (
            <div style={{
                height: '300px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Loader />
            </div>
        );
    }

    return (
        <div className="ebp-page">
            <style>{`
              /* Base layout — matches other admin pages */
              .ebp-page { background: #f9fafb; min-height: calc(100vh - 70px); }
              .main-content:has(.ebp-page) { background: #f9fafb; }
              .page-content:has(.ebp-page) { background: #f9fafb; }
              body:has(.ebp-page) .container-fluid,
              body:has(.ebp-page) .container {
                padding-left: 0 !important;
                padding-right: 0 !important;
              }
              body:has(.ebp-page) .page-content,
              body:has(.ebp-page) #layout-wrapper.no-topbar .page-content,
              body:has(.ebp-page) #layout-wrapper .page-content {
                padding-top: 0 !important;
                padding-bottom: 24px !important;
                padding-left: 16px !important;
                padding-right: 16px !important;
              }
              .ebp-page .row { margin-bottom: 0; }

              /* Sticky header */
              .ebp-page .page-header-sticky {
                padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
                margin: 0 -16px 12px !important;
                top: 0 !important;
                background: #f9fafb !important;
                border-bottom: 1px solid #eef2f7 !important;
                box-shadow: none !important;
                display: flex;
                align-items: center;
                gap: 10px;
              }
              @media (max-width: 767.98px) {
                .ebp-page .page-header-sticky {
                  padding: 10px 16px 8px !important;
                  margin: 0 -16px 8px !important;
                  position: static !important;
                }
              }
              .ebp-page .ebp-back {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                border-radius: 6px;
                border: 1px solid #e2e8f0;
                background: #fff;
                color: #475569;
                cursor: pointer;
                transition: all 0.15s ease;
                padding: 0;
                flex-shrink: 0;
              }
              .ebp-page .ebp-back:hover {
                border-color: #008695;
                color: #008695;
                background: rgba(0,134,149,0.06);
              }

              /* Card + form compact typography */
              .ebp-page .card {
                border: 1px solid #e2e8f0 !important;
                border-radius: 10px !important;
                box-shadow: 0 1px 2px rgba(15,23,42,0.03) !important;
                margin-bottom: 10px !important;
              }
              .ebp-page .card-body { padding: 12px 14px !important; }
              .ebp-page .form-label {
                font-size: 11px !important;
                font-weight: 700 !important;
                color: #475569 !important;
                letter-spacing: 0.03em;
                text-transform: uppercase;
                margin-bottom: 4px !important;
              }
              .ebp-page .form-control,
              .ebp-page .form-select {
                height: 32px !important;
                padding: 4px 10px !important;
                font-size: 12px !important;
                border: 1px solid #e2e8f0 !important;
                border-radius: 5px !important;
              }
              .ebp-page .form-control:focus,
              .ebp-page .form-select:focus {
                border-color: #008695 !important;
                box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
                outline: none;
              }
              .ebp-page .form-group.mb-3,
              .ebp-page .form-group.mb-4,
              .ebp-page .mb-3,
              .ebp-page .mb-4 { margin-bottom: 10px !important; }

              /* Brand image uploader */
              .ebp-page .ebp-image-uploader {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100%;
                min-height: 180px;
                border: 2px dashed #cbd5e1;
                border-radius: 10px;
                background: linear-gradient(180deg, #fbfcfd 0%, #f4f7fa 100%);
                cursor: pointer;
                overflow: hidden;
                transition: all 0.15s ease;
              }
              .ebp-page .ebp-image-uploader:hover {
                border-color: #008695;
                background: rgba(0,134,149,0.04);
              }
              .ebp-page .ebp-image-uploader .ebp-image-preview {
                width: 100%;
                height: 100%;
                max-height: 100%;
                object-fit: contain;
              }
              .ebp-page .ebp-image-uploader .ebp-image-overlay {
                position: absolute;
                inset: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                background: rgba(15,23,42,0.55);
                color: #fff;
                opacity: 0;
                transition: opacity 0.15s ease;
                font-size: 11px;
                font-weight: 600;
              }
              .ebp-page .ebp-image-uploader:hover .ebp-image-overlay { opacity: 1; }
              .ebp-page .ebp-image-empty {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 4px;
                text-align: center;
                padding: 12px;
              }
              .ebp-page .ebp-image-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 34px;
                height: 34px;
                border-radius: 8px;
                background: rgba(0,134,149,0.10);
                color: #008695;
                margin-bottom: 4px;
              }
              .ebp-page .ebp-image-title { font-size: 12px; font-weight: 600; color: #0f172a; }
              .ebp-page .ebp-image-sub { font-size: 10px; color: #94a3b8; font-weight: 500; letter-spacing: 0.02em; }

              /* Category pill */
              .ebp-page .category-pill {
                padding: 5px 12px !important;
                border-radius: 999px !important;
                font-size: 11px !important;
                font-weight: 500 !important;
                border: 1px solid #e2e8f0 !important;
                background: #fff !important;
                color: #475569 !important;
                cursor: pointer;
                transition: all 0.15s ease;
              }
              .ebp-page .category-pill:hover {
                border-color: #cbd5e1 !important;
                color: #0f172a !important;
              }
              .ebp-page .category-pill.selected {
                background: #008695 !important;
                border-color: #008695 !important;
                color: #fff !important;
                font-weight: 600 !important;
                box-shadow: 0 2px 6px rgba(0,134,149,0.22) !important;
              }

              /* Primary + secondary buttons */
              .ebp-page .ebp-primary-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 0 18px !important;
                border-radius: 6px !important;
                font-size: 12.5px !important;
                font-weight: 600 !important;
                height: 36px !important;
                background: #008695 !important;
                border: 1px solid #008695 !important;
                color: #fff !important;
                cursor: pointer;
                box-shadow: 0 3px 8px rgba(0,134,149,0.22);
                transition: all 0.15s ease;
                white-space: nowrap;
                flex-shrink: 0;
              }
              .ebp-page .ebp-primary-btn:hover:not(:disabled) {
                background: #006672 !important;
                border-color: #006672 !important;
              }
              .ebp-page .ebp-primary-btn:disabled { opacity: 0.7; cursor: not-allowed; }
              .ebp-page .ebp-secondary-btn {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 0 14px !important;
                border-radius: 6px !important;
                font-size: 12px !important;
                font-weight: 600 !important;
                height: 34px !important;
                background: #fff !important;
                border: 1px solid #e2e8f0 !important;
                color: #0f172a !important;
                cursor: pointer;
                transition: all 0.15s ease;
                white-space: nowrap;
                flex-shrink: 0;
              }
              .ebp-page .ebp-secondary-btn:hover:not(:disabled) {
                background: #f8fafc !important;
                border-color: #cbd5e1 !important;
              }

              /* Hidden legacy footer + mobile action bar */
              .ebp-page .ebp-inline-actions { display: none !important; }
              .ebp-page .ebp-mobile-actionbar { display: none; }
              @media (max-width: 575.98px) {
                .ebp-page .ebp-header-primary { display: none !important; }
                .ebp-page { padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px)); }
                .ebp-page .ebp-mobile-actionbar {
                  display: flex;
                  position: fixed;
                  left: 0; right: 0; bottom: 0;
                  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
                  background: #fff;
                  border-top: 1px solid #e2e8f0;
                  box-shadow: 0 -2px 8px rgba(15,23,42,0.05);
                  z-index: 1049;
                  gap: 8px;
                }
                .ebp-page .ebp-mobile-actionbar .ebp-primary-btn {
                  flex: 1;
                  height: 42px !important;
                  font-size: 13px !important;
                }
              }
            `}</style>

            <div className="page-header-sticky">
                <button
                    type="button"
                    className="ebp-back"
                    aria-label="Go back"
                    onClick={() => navigate(-1)}
                >
                    <IoMdArrowBack size={18} />
                </button>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.01em', flex: 1 }}>
                    {id ? 'Update' : 'Create'} Brand
                </h3>
                <button
                    type="button"
                    className="ebp-secondary-btn"
                    onClick={() => setShowSizeChartModal(true)}
                >
                    <BiPlus />
                    Edit Size Chart
                </button>
                <button
                    type="button"
                    className="ebp-primary-btn ebp-header-primary"
                    onClick={formik.handleSubmit}
                    disabled={isSavingSizeChart}
                >
                    {isSavingSizeChart ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {id ? 'Updating...' : 'Creating...'}
                        </>
                    ) : id ? (
                        <>
                            <FiCheckCircle size={14} strokeWidth={2} />
                            Update Brand
                        </>
                    ) : (
                        <>
                            <FiPlus size={14} strokeWidth={2.5} />
                            Create Brand
                        </>
                    )}
                </button>
            </div>
            <Container fluid className="p-0">

                {/* Brand Details Card */}
                <Card className="mb-4">
                    <Card.Body>
                        <Form onSubmit={formik.handleSubmit}>
                            <Row>
                                <Col lg={8}>
                                    {/* Brand Name */}
                                    <Form.Group className="mb-4" controlId="formBrandName">
                                        <Form.Label className="fw-bold">Brand Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter brand name"
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            isValid={formik.touched.name && !formik.errors.name}
                                            isInvalid={formik.touched.name && !!formik.errors.name}
                                            className="brand-name-input"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    {/* Available Categories */}
                                    <div className="mb-2">
                                        <Form.Label className="fw-bold">Available Categories</Form.Label>
                                        <div className="available-categories-container d-flex flex-wrap gap-2">
                                            {availableCategories.map((category) => (
                                                <div
                                                    key={category.value}
                                                    className={`category-pill ${selectedCategoryIds.has(category.value) ? 'selected' : ''}`}
                                                    onClick={() => handleCategorySelect(category)}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '20px',
                                                        border: '1px solid #dee2e6',
                                                        backgroundColor: selectedCategoryIds.has(category.value) ? '#f8f9fa' : 'white',
                                                        color: selectedCategoryIds.has(category.value) ? '#000' : '#6c757d',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        fontWeight: selectedCategoryIds.has(category.value) ? '600' : '400',
                                                        boxShadow: selectedCategoryIds.has(category.value) ? '0 0 0 2px rgba(0,0,0,0.1)' : 'none'
                                                    }}
                                                >
                                                    {category.label}
                                                    {selectedCategoryIds.has(category.value) && (
                                                        <span className="check-mark ms-2">✓</span>
                                                    )}
                                                </div>
                                            ))}

                                        </div>
                                    </div>

                                    {/* Selected Categories with Subcategories */}

                                    <div style={{
                                        marginTop: "30px",

                                    }}>

                                        <Form.Label className="fw-bold">Selected Categories</Form.Label>
                                        {formik.values.categories.length > 0 ? (

                                            <div style={{

                                                
                                                // padding: "16px",


                                                background: "#ffffffff",
                                                // border: "1px solid #dee2e6",

                                                borderRadius: "8px",

                                                color: "#6c757d",
                                                fontSize: "14px",
                                            }}>

                                                <Row>
                                                    {formik.values.categories.map((cat, index) => (
                                                        <Row className="my-2" key={index}>
                                                            <Form.Group>
                                                                <Form.Label className="fw-semibold" style={{ fontSize: "13px" }}>
                                                                    <span className="fw-semibold text-dark">{cat.selectedCategory?.label}</span>
                                                                    <MdDeleteOutline
                                                                        role="button"
                                                                        onClick={() => deleteCategory(cat.categoryId)}
                                                                        style={{ fontSize: '20px', color: 'red' }}
                                                                        className="delete_category_btn"
                                                                    />
                                                                </Form.Label>
                                                                <AsyncSelect
                                                                    onFocus={() => setIsFocusedStaff(true)}
                                                                    onBlur={() => setIsFocusedStaff(false)}
                                                                    loadOptions={(inputValue) =>
                                                                        loadOptionsSubCategory(inputValue, cat.categoryId)
                                                                    }
                                                                    defaultOptions={subcategoryOptions[cat.categoryId] || []}
                                                                    classNamePrefix="select"
                                                                    isClearable={true}
                                                                    placeholder="select subcategories"
                                                                    onChange={(selectedOption) => {
                                                                        const updatedCategories = [...formik.values.categories];
                                                                        updatedCategories[index] = {
                                                                            ...updatedCategories[index],
                                                                            subCategories: selectedOption ?
                                                                                selectedOption.map(opt => opt.value) : [],
                                                                            selectedSubCategory: selectedOption,
                                                                        };
                                                                        formik.setFieldValue('categories', updatedCategories);
                                                                    }}
                                                                    value={cat.selectedSubCategory || []}
                                                                    noOptionsMessage={customNoOptionsMessage}
                                                                    isMulti
                                                                    styles={{
                                                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                                                        control: (base) => ({
                                                                            ...base,
                                                                            border: "1px solid #ced4da",
                                                                            borderRadius: "8px",
                                                                            minHeight: "40px",
                                                                            fontSize: "13px",
                                                                        }),
                                                                        valueContainer: (base) => ({
                                                                            ...base,
                                                                            display: "flex",
                                                                            flexWrap: "nowrap",
                                                                            overflowX: "auto",
                                                                            overflowY: "hidden",
                                                                            scrollbarWidth: "none",
                                                                            msOverflowStyle: "none",
                                                                            "::-webkit-scrollbar": {
                                                                                display: "none",
                                                                            },
                                                                        }),
                                                                        input: (base) => ({
                                                                            ...base,
                                                                            minWidth: "80px",
                                                                            flexShrink: 0,
                                                                        }),
                                                                        multiValue: (base) => ({
                                                                            ...base,
                                                                            backgroundColor: "#288d9dff", // purple
                                                                            color: "white",
                                                                            borderRadius: "12px",
                                                                            padding: "0 4px",
                                                                            flexShrink: 0,
                                                                        }),
                                                                        multiValueLabel: (base) => ({
                                                                            ...base,
                                                                            color: "white",
                                                                            fontSize: "12px",
                                                                        }),
                                                                        multiValueRemove: (base) => ({
                                                                            ...base,
                                                                            color: "white",
                                                                            ':hover': {
                                                                                backgroundColor: "#288d9dff",
                                                                                color: "white",
                                                                            },
                                                                        }),
                                                                    }}
                                                                />
                                                                {/* <div style={{ color: '#DC3544', fontSize: "12px" }}>
                                                                {formik.errors.categories?.[index]?.subCategories}
                                                            </div> */}
                                                            </Form.Group>
                                                        </Row>
                                                    ))}
                                                </Row>
                                            </div>
                                        ) : <div
                                            style={{

                                                height: "200px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                background: "#ffffffff",
                                                border: "1px solid #dee2e6",
                                                borderRadius: "8px",
                                                padding: "10px",
                                                color: "#6c757d",
                                                fontSize: "14px",
                                            }}
                                        >
                                            No categories selected yet.
                                        </div>
                                        }


                                    </div>

                                </Col>




                                <Col lg={4} className="order-first order-lg-last mb-3 mb-lg-0 d-flex flex-column">
                                    <Form.Label style={{ marginBottom: 6 }}>Brand Image</Form.Label>
                                    <input
                                        type="file"
                                        id="brand-image-upload"
                                        onChange={handleImageChange}
                                        accept="image/jpeg,image/png,image/gif"
                                        style={{ display: 'none' }}
                                    />
                                    <label
                                        htmlFor="brand-image-upload"
                                        className="ebp-image-uploader flex-grow-1"
                                    >
                                        {formik.values.image_view ? (
                                            <>
                                                <img
                                                    src={formik.values.image_view}
                                                    alt="Brand"
                                                    className="ebp-image-preview"
                                                />
                                                <div className="ebp-image-overlay">
                                                    <BiPlus size={18} />
                                                    <span>Change image</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="ebp-image-empty">
                                                <span className="ebp-image-icon">
                                                    <BiPlus size={20} />
                                                </span>
                                                <span className="ebp-image-title">Upload brand image</span>
                                                <span className="ebp-image-sub">PNG · JPG · GIF</span>
                                            </div>
                                        )}
                                    </label>
                                    {formik.errors.image && (
                                        <div className="text-danger" style={{ fontSize: 10.5, marginTop: 4 }}>
                                            {formik.errors.image}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>



                {/* Legacy inline action bar — hidden; header + mobile bar take over */}
                <div className="ebp-inline-actions">
                    <Button className="request_btn" type="button" onClick={formik.handleSubmit} disabled={isSavingSizeChart}>
                        {isSavingSizeChart ? 'Saving...' : (id ? 'Update Brand' : 'Create Brand')}
                    </Button>
                </div>
            </Container>

            {/* Mobile-only fixed bottom action bar */}
            <div className="ebp-mobile-actionbar">
                <button
                    type="button"
                    className="ebp-primary-btn"
                    onClick={formik.handleSubmit}
                    disabled={isSavingSizeChart}
                >
                    {isSavingSizeChart ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            {id ? 'Updating...' : 'Creating...'}
                        </>
                    ) : id ? (
                        <>
                            <FiCheckCircle size={15} strokeWidth={2} />
                            Update Brand
                        </>
                    ) : (
                        <>
                            <FiPlus size={15} strokeWidth={2.5} />
                            Create Brand
                        </>
                    )}
                </button>
            </div>

            <SizeChartModal
                
                show={showSizeChartModal}
                handleClose={() => setShowSizeChartModal(false)}
                categories={formik.values.categories}
                sizeChartData={sizeChartData}
                setSizeChartData={setSizeChartData}
                loading={isSavingSizeChart}
                sizeOptions={sizeChartMeta.sizes}
                sizeTypes={sizeChartMeta.sizeTypes}
                selectedBrandId={id}
            />
        </div>
    );
};

export default EditBrandPage;